import { h, Component, ComponentChildren, createRef } from "preact";

import IconMagnifier from "../Icons/IconMagnifier";

import classname from "../../foundation/utils/classname";
import { highlightInput } from "../../foundation/utils/platform";
import { cmdKey, ctrlOrWinKey } from "../../foundation/utils/keyboard";
import { stopPropagation, preventDefault } from "../../foundation/utils/legacy";
import { Keyboard } from "../../foundation/enums";

import styles from "./FilterInput.module.css";

interface FilterInputProps {
    class?: string;
    value: string;
    placeholder?: string;
    placeholderWidth?: number;
    disabled?: boolean;
    icon?: ComponentChildren;
    shouldCaptureSearchShortcut?: boolean;
    filterDelay?: number;
    autoFocus?: boolean;
    onFilter: (value: string) => void;
    onBlur?: () => void;
    onFocus?: () => void;
}

interface State {
    focusWithin: boolean;
    value: string;
}

class FilterInput extends Component<FilterInputProps, State> {
    filterTimeout: ReturnType<typeof setTimeout> | null = null;
    inputRef = createRef<HTMLInputElement>();

    constructor(props: FilterInputProps) {
        super(props);

        this.state = {
            focusWithin: false,
            value: props.value
        };
    }

    handleBlur = () => {
        const { onBlur } = this.props;

        onBlur?.();

        this.setState({ focusWithin: false });
    };

    handleFocus = () => {
        const { onFocus } = this.props;

        onFocus?.();

        this.setState({ focusWithin: true });
    };

    handleInput = (ev: Event) => {
        const { filterDelay, onFilter } = this.props;

        this.setState({
            value: (ev.target as HTMLInputElement).value
        });

        if (this.filterTimeout) {
            clearTimeout(this.filterTimeout);
        }

        this.filterTimeout = setTimeout(() => {
            onFilter((ev.target as HTMLInputElement).value);
        }, filterDelay);
    };

    handleKeyDown = (ev: KeyboardEvent) => {
        if (ev.key !== Keyboard.Key.Escape) {
            return;
        }

        ev.preventDefault();
        ev.stopPropagation();

        if (this.filterTimeout) {
            clearTimeout(this.filterTimeout);
        }

        if ((ev.target as HTMLInputElement).value) {
            this.setState({ value: "" });

            this.props.onFilter("");
        } else {
            (ev.target as HTMLInputElement).blur();
        }
    };

    handleSearchShortcut = (ev: KeyboardEvent) => {
        if (cmdKey(ev) && !(ctrlOrWinKey(ev) || ev.shiftKey || ev.altKey) && ev.key === Keyboard.Key.f) {
            ev.preventDefault();

            highlightInput(this.inputRef.current);
            this.inputRef.current!.scrollIntoView();
        }
    };

    override componentDidMount() {
        if (this.props.shouldCaptureSearchShortcut) {
            document.addEventListener("keydown", this.handleSearchShortcut);
        }

        if (this.props.autoFocus) {
            this.inputRef.current?.focus();
        }
    }

    override componentWillReceiveProps(nextProps: FilterInputProps) {
        if (typeof nextProps.value !== "undefined" && nextProps.value !== this.props.value) {
            this.setState({ value: nextProps.value });
        }
    }

    override componentWillUnmount() {
        document.removeEventListener("keydown", this.handleSearchShortcut);
    }

    render() {
        const {
            class: cssClass,
            placeholder,
            placeholderWidth,
            disabled,
            icon
        } = this.props;
        const { focusWithin, value } = this.state;

        const classes = classname(styles.filterInput, "noSelect", cssClass, { [styles.focusWithin]: focusWithin });
        const style = (focusWithin || value) ? {} : { width: placeholderWidth };
        return (
            <label
                class={classes}
                onClick={stopPropagation}
                onMouseDown={preventDefault}>
                {icon}
                <input
                    style={style}
                    type="search"
                    placeholder={placeholder}
                    value={value}
                    ref={this.inputRef}
                    disabled={disabled}
                    spellcheck={false}
                    onBlur={this.handleBlur}
                    onFocus={this.handleFocus}
                    onKeyDown={this.handleKeyDown}
                    onInput={this.handleInput}
                    onMouseDown={stopPropagation}/>
            </label>
        );
    }
}

FilterInput.defaultProps = {
    icon: <IconMagnifier/>,
    shouldCaptureSearchShortcut: true,
    filterDelay: 200
};

export { FilterInputProps };
export default FilterInput;
